import { useEffect, useRef } from 'react'
import { marked } from 'marked'
import hljs from 'highlight.js'
// 使用 VSCode 风格的代码高亮主题
import 'highlight.js/styles/github.css'
import '@/styles/markdown.css'
// KaTeX 数学公式渲染
import 'katex/dist/katex.min.css'
import { InlineMath, BlockMath } from 'react-katex'
import { createRoot } from 'react-dom/client'

interface MarkdownRendererProps {
  content: string
  className?: string
}

const MarkdownRenderer = ({
  content,
  className = '',
}: MarkdownRendererProps) => {
  const containerRef = useRef<HTMLDivElement>(null)

  // 语言映射表
  const languageMap: Record<string, string> = {
    javascript: 'JS',
    typescript: 'TS',
    python: 'Python',
    java: 'Java',
    cpp: 'C++',
    c: 'C',
    csharp: 'C#',
    php: 'PHP',
    ruby: 'Ruby',
    go: 'Go',
    rust: 'Rust',
    swift: 'Swift',
    kotlin: 'Kotlin',
    scala: 'Scala',
    html: 'HTML',
    css: 'CSS',
    scss: 'SCSS',
    less: 'Less',
    sass: 'Sass',
    json: 'JSON',
    xml: 'XML',
    yaml: 'YAML',
    toml: 'TOML',
    ini: 'INI',
    bash: 'Bash',
    shell: 'Shell',
    powershell: 'PowerShell',
    sql: 'SQL',
    markdown: 'MD',
    text: 'Text',
  }

  // 添加复制按钮到代码块
  const addCopyButtons = () => {
    const codeBlocks = containerRef.current?.querySelectorAll(
      '.markdown-content pre code'
    )

    codeBlocks?.forEach((codeBlock) => {
      const pre = codeBlock.parentElement
      if (!pre || pre.querySelector('.copy-btn')) return

      const copyBtn = document.createElement('button')
      copyBtn.className = 'copy-btn'
      copyBtn.innerHTML = '<i class="fas fa-copy"></i><span>复制</span>'
      copyBtn.title = '复制代码'

      copyBtn.addEventListener('click', async () => {
        try {
          await navigator.clipboard.writeText(codeBlock.textContent || '')
          copyBtn.innerHTML = '<i class="fas fa-check"></i><span>已复制</span>'
          copyBtn.title = '已复制'

          setTimeout(() => {
            copyBtn.innerHTML = '<i class="fas fa-copy"></i><span>复制</span>'
            copyBtn.title = '复制代码'
          }, 2000)
        } catch (error) {
          console.error('复制失败:', error)
        }
      })

      pre.appendChild(copyBtn)
    })
  }

  // 添加语言标识到代码块
  const addLanguageLabels = () => {
    const codeBlocks = containerRef.current?.querySelectorAll(
      '.markdown-content pre code'
    )

    codeBlocks?.forEach((codeBlock) => {
      const pre = codeBlock.parentElement
      if (!pre || pre.querySelector('.language-label')) return

      // 获取语言标识
      const language =
        codeBlock.className.match(/language-(\w+)/)?.[1] || 'text'
      const languageLabel = languageMap[language] || language.toUpperCase()

      const label = document.createElement('div')
      label.className = 'language-label'
      label.textContent = languageLabel

      pre.appendChild(label)
    })
  }

  // 渲染数学公式
  const renderMathFormulas = () => {
    // 渲染行内公式
    const inlineMathElements =
      containerRef.current?.querySelectorAll('.math-inline')
    inlineMathElements?.forEach((element) => {
      const formula = element.getAttribute('data-formula')
      if (formula) {
        try {
          // 清空元素内容
          element.innerHTML = ''

          const mathComponent = (
            <InlineMath errorColor="#cc0000">{formula}</InlineMath>
          )

          // 如果没有 root，则创建；有的话直接用
          if (!(element as any)._reactRoot) {
            ;(element as any)._reactRoot = createRoot(element)
          }

          ;(element as any)._reactRoot.render(mathComponent)
        } catch (error) {
          console.error('行内公式渲染失败:', error)
          element.textContent = `$${formula}$`
        }
      }
    })

    // 渲染块级公式
    const blockMathElements =
      containerRef.current?.querySelectorAll('.math-block')
    blockMathElements?.forEach((element) => {
      const formula = element.getAttribute('data-formula')
      if (formula) {
        try {
          // 清空元素内容
          element.innerHTML = ''

          const mathComponent = (
            <BlockMath errorColor="#cc0000">{formula}</BlockMath>
          )

          // 如果没有 root，则创建；有的话直接用
          if (!(element as any)._reactRoot) {
            ;(element as any)._reactRoot = createRoot(element)
          }

          ;(element as any)._reactRoot.render(mathComponent)

          // 检测公式是否超出容器宽度
          setTimeout(() => {
            const katexElement = element.querySelector('.katex-display')
            if (katexElement) {
              const containerWidth = element.clientWidth
              const contentWidth = katexElement.scrollWidth

              if (contentWidth > containerWidth) {
                element.classList.add('overflowing')
              } else {
                element.classList.remove('overflowing')
              }
            }
          }, 100)
        } catch (error) {
          console.error('块级公式渲染失败:', error)
          element.textContent = `$$${formula}$$`
        }
      }
    })
  }

  // 处理数学公式的函数
  const processMathFormulas = (text: string): string => {
    // 处理行内公式 $...$
    text = text.replace(/\$([^$\n]+?)\$/g, (match, formula) => {
      try {
        // 使用占位符标记行内公式
        return `<span class="math-inline" data-formula="${formula.replace(
          /"/g,
          '&quot;'
        )}"></span>`
      } catch (error) {
        console.error('行内公式处理失败:', error)
        return match
      }
    })

    // 处理块级公式 $$...$$
    text = text.replace(/\$\$([\s\S]+?)\$\$/g, (match, formula) => {
      try {
        // 使用占位符标记块级公式
        return `<div class="math-block" data-formula="${formula.replace(
          /"/g,
          '&quot;'
        )}"></div>`
      } catch (error) {
        console.error('块级公式处理失败:', error)
        return match
      }
    })

    return text
  }

  useEffect(() => {
    // 配置 marked 选项
    marked.setOptions({
      breaks: true,
      gfm: true,
      highlight: function (code: string, lang: string) {
        if (lang && hljs.getLanguage(lang)) {
          try {
            return hljs.highlight(code, { language: lang }).value
          } catch (err) {
            console.error('代码高亮失败:', err)
          }
        }
        // 如果没有指定语言或语言不支持，使用自动检测
        return hljs.highlightAuto(code).value
      },
    } as any)

    if (containerRef.current) {
      // 先处理数学公式，再渲染 Markdown
      const processedContent = processMathFormulas(content)
      const html = marked(processedContent) as string
      containerRef.current.innerHTML = html

      // 延迟处理代码块和数学公式，确保DOM已更新
      setTimeout(() => {
        // 只对当前容器内的代码块进行高亮，避免重复处理
        const codeBlocks = containerRef.current?.querySelectorAll('pre code')
        codeBlocks?.forEach((codeBlock) => {
          if (!codeBlock.classList.contains('hljs')) {
            hljs.highlightElement(codeBlock as HTMLElement)
          }
        })
        addCopyButtons()
        addLanguageLabels()

        // 渲染数学公式
        renderMathFormulas()
      }, 100)
    }
  }, [content])

  return <div ref={containerRef} className={`markdown-content ${className}`} />
}

export default MarkdownRenderer
